import { ColumnListDto } from '@common/dto/column/list-dto';
import { HttpSuccessDto } from '@common/dto/http';
import { InviteFilterInvitesStatus } from '@common/dto/invite/status';
import { InviteListDto } from '@common/dto/invite/list-dto';
import client, { clientTransformed } from '@common/utils/rest';
import { InvitesExportOptionKeys, InvitesExportType } from '../components/invites-export/invites-export.types';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { PedigreeListDto } from '@common/dto/pedigree/list-dto';
import { InviteStatusIndividualListDto } from '../dto/individuals/list-dto';
import { InviteMetadataListDto } from '../dto/invite/new-invite-check/list-dto';
import { SkipLogicCondition } from '@common/components/skip-logic/skip-logic.types';
import { QueryManagementFormat } from '@common/components/query-management';

const URL_PREFIX = 'api/invites';

const getUrl = (path: string) => {
  return `${URL_PREFIX}/${path}`;
};

export type GetInvitesRequestFiltering = InviteStatusFilter & {
  search?: string;
  filterApplied: boolean;
  columnFilters: {};
  columns: Array<ColumnListDto> ;
  clauses?:Array<SkipLogicCondition>;
};

export type GetInvitesRequestPagination = {
  start: number;
  size: number;
};

export type InviteStatusFilterWithinDate = `${'DAYS' | 'WEEKS'}_${number}`;

export type InviteStatusFilter = {
  questionnaires?: GetInvitesFilterDto['questionnaires'];
  inviteStatuses?: GetInvitesFilterDto['inviteStatuses'];
  invitedBy?: GetInvitesFilterDto['invitedBy'];
  dateSent?: InviteStatusFilterWithinDate;
  dateStarted?: InviteStatusFilterWithinDate;
  dateCompleted?: InviteStatusFilterWithinDate;
};

export type GetInvitesRequestParams = {
  filtering: GetInvitesRequestFiltering;
  pagination: GetInvitesRequestPagination;
};

export const getInvites = ({ filtering, pagination }: GetInvitesRequestParams): Promise<{ data: Array<InviteListDto>; totalCount: number; }> => {
  return clientTransformed({
    path: getUrl('smartlist'),
    method: 'POST',
		entity: {
			...filtering,
			...pagination,
		},
  }).then(
    (response: HttpSuccessDto<{ data: Array<InviteListDto>; totalCount: number; }>) => {
      return response.entity;
    },
  );
};

export type InvitesExportParams = Record<InvitesExportOptionKeys, boolean> & {
  delimiter: string;
  exportType: InvitesExportType;
  smartList: {
    columns: Array<ColumnListDto>;
  }
}

export const exportInvites = (params: InvitesExportParams): Promise<{ entity: string, filename: string }> => {
  return client({
    path: getUrl('export'),
    method: 'POST',
		entity: params,
  }).then(
    (response: HttpSuccessDto<string, { 'Content-Disposition': string }>) => {
      const contentDisposition = response.headers['Content-Disposition'].split('=');

      return {
        entity: response.entity,
        filename: contentDisposition[contentDisposition.length - 1]?.trim()
      };
    },
  );
};

type GetInvitesFilterQuestionnaireList = {
  id: number;
  fhq4: boolean;
  name: string;
};

export type GetInvitesFilterDto = {
  questionnaires: Array<GetInvitesFilterQuestionnaireList>;
  inviteStatuses: Array<InviteFilterInvitesStatus>;
  invitedBy: Array<string>;
};

export const getInvitesFilter = () => {
  return clientTransformed({
    path: getUrl('filters'),
    method: 'GET',
  }).then(
    (response: HttpSuccessDto<GetInvitesFilterDto>) => {
      return response.entity;
    },
  );
};

export type InvitesGetIndividualsColumnsRequestParams = {
  questionnaireId: number;
  fhq4: boolean;
};

export const getIndividualsColumns = (params: InvitesGetIndividualsColumnsRequestParams): Promise<Array<ColumnListDto>> => {
	return clientTransformed({
    path: getUrl('searchIndividuals/columns'),
    method: 'GET',
    params,
  }).then(
    (response: HttpSuccessDto<Array<ColumnListDto>>) => {
      return response.entity;
    },
  );
};

export type FhqInviteRelativesGetParams = {
  id: QuestionnaireListDto['questionnaireId'];
  fhq4: boolean;
  familyId: PedigreeListDto['id'];
  start: number;
  length: number;
}

export const getFhqInviteRelatives = (params: FhqInviteRelativesGetParams): Promise<InviteStatusIndividualListDto> => {
  return clientTransformed({
    path: getUrl('fhqInviteRelatives'),
    method: 'GET',
    params,
  }).then((response: HttpSuccessDto<InviteStatusIndividualListDto>) => {
    return response.entity;
  });
};

export type FhqInviteIndividualsGetParams = {
  id: QuestionnaireListDto['questionnaireId'];
  fhq4: boolean;
  start: number;
  length: number;
  format?: Partial<QueryManagementFormat> & {
    clauses: Array<Omit<SkipLogicCondition, 'id'>>;
  };
}

export const getFhqInviteIndividuals = (params: FhqInviteIndividualsGetParams): Promise<InviteStatusIndividualListDto> => {
  const { format, ...rest } = params;
  return clientTransformed({
    path: getUrl("fhqInviteIndividuals"),
    method: "POST",
    params: rest,
    entity: format,
  }).then((response: HttpSuccessDto<InviteStatusIndividualListDto>) => {
    return response.entity;
  });
};

export const getInviteMetadata = (params: { familyId: number; globalId: string }): Promise<InviteMetadataListDto> => {
  return clientTransformed({
    path: getUrl('checkInviteData'),
    method: 'GET',
    params,
  }).then(
    (response: HttpSuccessDto<InviteMetadataListDto>) => {
      return response.entity;
    },
  );
}